/**
 * The copy for the controls that ask a customer to complete a face check.
 *
 * Held here rather than in the view that composes them, for the same reason `statements.ts` holds
 * the check cards' prose: a call site that re-derives wording can get it wrong, and no test would
 * catch it. Decided 2026-08-07; the reasoning is in the request state UX brief.
 */

export type TRequestActionCopy = {
  /** The button's visible label. */
  label: string;
  /** The `data-action` value for that same button, per ADR 71. */
  dataAction: string;
};

/**
 * The label and data attribute for the button that asks a customer to complete a face check.
 *
 * **The button is offered at every level, and withheld only when the caller may not press it.**
 * There is no *outcome* that makes asking pointless: even at `High`, where the level cannot improve
 * because it is that axis's ceiling and no risk attribute reads how old a check is, a client may
 * still want a fresh one — ongoing due diligence is periodic, and a client who doubts the earlier
 * capture is not chasing a bigger number. The design says "not the thing to act on" by leaving the
 * button outlined away from `Low`, which is the honest lever for that.
 *
 * Until 2026-08-25 this paragraph read "the button is always offered… removing it would hide a
 * control for a reason the reader cannot see", and that reasoning still holds for every question it
 * was written about, all of which are about the customer's outcome. It does not reach the question
 * VME-4156 asked, which is about the *caller*: person-api refuses a face check task from an
 * organisation the customer has not authorised, so the control was being offered and then refused.
 * Decided 2026-08-25 (VME-4156, owner Leo Zhao): where the request will be refused, the view
 * withholds the control rather than presenting it. The cost — a control that is present for one
 * customer and absent for the next, with nothing on the card saying why in most states — was
 * weighed and accepted; see the ticket's decision log. This resolver is unchanged by that: it still
 * answers only "which verb", and it is the view that decides whether to render a button at all.
 *
 * **The discriminant is whether a task is open, not whether one ever existed.** The verb describes
 * what pressing it does rather than reciting history the client cannot act on: with a task
 * outstanding the customer already has one waiting, so asking reads as a repeat; with none, it
 * starts one. A customer asked once and long since finished therefore reads `REQUEST NOW`, because
 * that is what the press would do.
 *
 * This also means the verb, the request link and the no-requests line all hang off the one boolean,
 * so they cannot contradict each other. What it deliberately drops, never-asked as against asked
 * and completed, has no bearing on what the client does next, and `EvidenceProvenance` states it
 * exactly in the evidence dialog for anyone who needs it.
 *
 * The label and the attribute are returned together so they cannot drift apart. Changing one
 * without the other leaves a test or an analytics query selecting on a value the button no longer
 * carries, which fails silently.
 */
export function requestActionCopy(hasOutstandingRequest: boolean): TRequestActionCopy {
  return hasOutstandingRequest
    ? { label: 'REQUEST AGAIN', dataAction: 'request-again' }
    : { label: 'REQUEST NOW', dataAction: 'request-now' };
}

/**
 * What occupies the request link's line on a check card when no face check task is open.
 *
 * **Plural**, because a customer can hold several face check tasks at once. They close together when
 * a check is confirmed, so the singular would quietly claim there is only ever one request to be
 * outstanding.
 *
 * **No full stop**, because this is a status label sitting in a control slot beside a button, not a
 * sentence in the prose region the card's guidance paragraph owns.
 *
 * It says nothing about where the customer has got to, deliberately. On a card carrying an outcome
 * the guidance sentence above already says the check was completed, and the request history belongs
 * to the evidence dialog's provenance row rather than to this line.
 */
export const NO_OUTSTANDING_REQUESTS = 'No outstanding requests';
